import React from 'react';
import { Place } from '../../../types/results.types';
import { BalanceResponse } from '../../../types/balance.types';
import { toTitleCase } from '../../../utils/formatting';
import ContactCell from '../ContactCell/ContactCell';
import EmailStatusCell from '../EmailStatusCell/EmailStatusCell';
import MatchStrengthCell from '../MatchStrengthCell/MatchStrengthCell';
import PhoneContactCell from '../PhoneContactCell/PhoneContactCell';
import GoogleAdsSignalBadge from '../GoogleAdsSignalBadge/GoogleAdsSignalBadge';


interface CompactResultRowProps {
  place: Place;
  onViewDetails: () => void;
  searchId?: number;
  isSelected: boolean;
  onToggle: (id: number) => void;
  hints?: boolean;
  showRating?: boolean;
  isFirstRow?: boolean;
  showDrawerCoachmark?: boolean;
  onDismissDrawerCoachmarkGotIt?: () => void;
  balance?: BalanceResponse | null;
  onUpgradeClick?: () => void;
  currentPlan?: string | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
  shortLabels?: Record<string, string>;
  showWebsiteMatch?: boolean;
  isNoWebsiteSearch?: boolean;
  selectable?: boolean;
}


const CompactResultRow: React.FC<CompactResultRowProps> = ({
  place,
  onViewDetails,
  searchId,
  isSelected,
  onToggle,
  hints = true,
  showRating = true,
  isFirstRow = false,
  showDrawerCoachmark = false,
  onDismissDrawerCoachmarkGotIt,
  balance,
  onUpgradeClick,
  currentPlan,
  autoFindMode,
  autoFindSuccessLimit,
  autoFindSuccessCount,
  autoFindAttemptCount,
  autoFindNoEmailCount,
  autoFindCatchAllCount,
  autoFindGuardrailReason,
  shortLabels = {},
  showWebsiteMatch = false,
  isNoWebsiteSearch = false,
  selectable = true,
}) => {
  const mainIndustry =
    place.main_industry ||
    ((place.industries?.length ?? 0) > 0 ? place.industries![0].name : null);

  const handleRowClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) return;
    onViewDetails();
  };

  return (
    <tr
      onClick={handleRowClick}
      className={`border-b cursor-pointer transition-all duration-100
        ${selectable && isSelected
          ? 'bg-[#FFF9F9] shadow-[inset_3px_0_0_#8A3033] border-[#F1DDDD]'
          : 'bg-white hover:bg-[#FAFAF8] border-[#F4F2EE]'
        }`}
    >
      {/* Checkbox */}
      {selectable && (
        <td
          className="w-10 px-3 py-4"
          onClick={e => { e.stopPropagation(); onToggle(place.id); }}
        >
          <div
            className={`w-[17px] h-[17px] rounded flex items-center justify-center border cursor-pointer transition-all flex-shrink-0
              ${isSelected ? 'bg-[#8A3033] border-[#8A3033]' : 'bg-white border-[#D1D5DB]'}`}
          >
            {isSelected && (
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l2.5 2.5L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </td>
      )}

      {/* Business */}
      <td className={`px-4 py-4 min-w-[200px] max-w-[260px] ${isFirstRow ? 'relative z-[45]' : ''}`}>
        {showDrawerCoachmark && (
          <div className="hidden md:block absolute left-0 top-full mt-2 w-64 max-w-[min(16rem,calc(100vw-2rem))] bg-[#2D3142] text-white rounded-xl shadow-xl p-4 z-[60] pointer-events-auto">
            <div className="absolute -top-1.5 left-8 w-3 h-3 bg-[#2D3142] rotate-45" />
            <p className="font-bold text-sm mb-1">👋 Open company details</p>
            <p className="text-xs leading-relaxed opacity-90">
              {isNoWebsiteSearch
                ? 'Click a row or the business name to see the full profile, phone numbers, and public-record contacts.'
                : 'Click a row or the business name to see the full profile, contacts, and emails in the side panel.'}
            </p>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onDismissDrawerCoachmarkGotIt?.(); }}
              className="mt-3 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
            >
              Got it
            </button>
          </div>
        )}

        <div className="flex items-center gap-2.5">
          {/* Logo monogram */}
          <div
            className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center text-[11px] font-extrabold"
            style={{ background: 'rgba(138,48,51,0.08)', color: '#8A3033' }}
          >
            {(place.name.match(/\b[A-Z]/g)?.slice(0, 2).join('') || place.name.slice(0, 2).toUpperCase())}
          </div>

          <div className="min-w-0">
            <button
              onClick={e => { e.stopPropagation(); onViewDetails(); }}
              className="text-[13px] font-semibold text-[#2D3142] hover:text-[#8A3033] text-left truncate block max-w-[190px] transition-colors"
              title={place.name}
            >
              {toTitleCase(place.name)}
            </button>

            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {mainIndustry && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 whitespace-nowrap"
                  style={{ background: 'rgba(138,48,51,0.08)', color: '#8A3033' }}
                >
                  {mainIndustry}
                </span>
              )}
              {place.is_previously_seen && (
                <span
                  className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700"
                  title="Previously seen in another search — no extra charge"
                >
                  FREE
                </span>
              )}
            </div>

            <GoogleAdsSignalBadge signal={place.lead_signals?.google_ads_signal} />

            {place.address && place.address !== 'None' && (
              <div className="text-[11px] text-[#9CA3AF] mt-0.5 truncate max-w-[190px]">
                {toTitleCase(place.address)}
              </div>
            )}
          </div>
        </div>
      </td>

      {showWebsiteMatch && (
        <td className="px-4 py-4 min-w-[170px]">
          <MatchStrengthCell fit={place.website_fit} shortLabels={shortLabels} />
        </td>
      )}

      {isNoWebsiteSearch && (
        <td className="px-4 py-4 min-w-[190px]">
          <PhoneContactCell place={place} />
        </td>
      )}

      {/* Decision makers — ContactCell */}
      <td className="px-4 py-4 min-w-[200px]">
        <ContactCell place={place} hints={hints} publicRecordsOnly={isNoWebsiteSearch} />
      </td>

      {/* Emails — EmailStatusCell (also acts as CTA) */}
      {!isNoWebsiteSearch && (
        <td className="px-4 py-4 min-w-[220px]">
          <EmailStatusCell
            place={place}
            balance={balance ?? null}
            searchId={searchId}
            currentPlan={currentPlan}
            autoFindMode={autoFindMode}
            autoFindSuccessLimit={autoFindSuccessLimit}
            autoFindSuccessCount={autoFindSuccessCount}
            autoFindAttemptCount={autoFindAttemptCount}
            autoFindNoEmailCount={autoFindNoEmailCount}
            autoFindCatchAllCount={autoFindCatchAllCount}
            autoFindGuardrailReason={autoFindGuardrailReason}
            onViewDetails={onViewDetails}
            onUpgradeClick={onUpgradeClick}
          />
        </td>
      )}

      {/* Rating — moved last */}
      {showRating && (
        <td className="px-4 py-4 min-w-[80px]">
          {place.review_rating > 0 ? (
            <div
              className="inline-flex items-center gap-1"
              aria-label={`${place.review_rating} stars · ${place.review_count} reviews`}
              title={`${place.review_rating} stars · ${place.review_count} reviews`}
            >
              <svg className="w-3 h-3 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.173 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-[12px] text-[#6B7280] font-medium">{place.review_rating}</span>
            </div>
          ) : (
            <span className="text-[12px] text-[#D1D5DB]">—</span>
          )}
        </td>
      )}
    </tr>
  );
};

export default CompactResultRow;
